import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Section({ title, children }) {
  return (
    <div style={{ border: '1px solid #EAE6E1', borderRadius: 8, marginBottom: 16, background: 'white' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F0EDE9', fontSize: 13, fontWeight: 600, color: '#333' }}>
        {title}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#111' }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colours = {
    active: { bg: '#E8F5EC', fg: '#1a7f3c' },
    error: { bg: '#FBE9E9', fg: '#b91c1c' },
    paused: { bg: '#FDF6E3', fg: '#a16207' },
    disconnected: { bg: '#F0F0F0', fg: '#666' },
    pending: { bg: '#EEF2FF', fg: '#4338ca' },
  };
  const c = colours[status] || colours.disconnected;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500 }}>
      {status || 'unknown'}
    </span>
  );
}

export default function MailboxDetail() {
  const { mailboxId } = useParams();
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [mb, setMb] = useState(null);
  const [client, setClient] = useState(null);
  const [health, setHealth] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // editable fields
  const [displayName, setDisplayName] = useState('');
  const [signatureHtml, setSignatureHtml] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [dailyCap, setDailyCap] = useState(50);
  const [dailyCapCeiling, setDailyCapCeiling] = useState(500);
  const [rampPct, setRampPct] = useState(20);
  const [warmupEnabled, setWarmupEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, { headers });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setMb(data);
      setDisplayName(data.display_name || '');
      setSignatureHtml(data.signature_html || '');
      setSignatureText(data.signature_text || '');
      setDailyCap(data.daily_cap ?? 50);
      setDailyCapCeiling(data.daily_cap_ceiling ?? 500);
      setRampPct(data.ramp_pct ?? 20);
      setWarmupEnabled(!!data.warmup_enabled);

      // fetch client info for header
      if (data.client_id) {
        const cres = await fetch(`${API_BASE}/clients/${data.client_id}`, { headers });
        if (cres.ok) setClient(await cres.json());
      }

      // fetch health (non-blocking)
      fetch(`${API_BASE}/mailboxes/${mailboxId}/health`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(h => setHealth(h))
        .catch(() => {});

      // fetch persona memberships (non-blocking)
      fetch(`${API_BASE}/mailboxes/${mailboxId}/personas`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(p => setPersonas(Array.isArray(p) ? p : []))
        .catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mailboxId, getToken]);

  useEffect(() => { load(); }, [load]);

  const save = async (fields) => {
    setSaving(true);
    setSavedMsg('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setSavedMsg('Saved');
      setTimeout(() => setSavedMsg(''), 2000);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const action = async (path, method = 'POST') => {
    if (!window.confirm(`Confirm: ${path}?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Action failed (${res.status})`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this mailbox? Tokens will be wiped. Row kept for audit.')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Disconnect failed (${res.status})`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const hardDelete = async () => {
    if (!window.confirm('PERMANENT DELETE. The mailbox row will be removed entirely. Continue?')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}?hard=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      navigate(isGlobalMode ? '/mail/mailboxes' : `/clients/${clientId}/mail/mailboxes`);
    } catch (e) {
      setError(e.message);
    }
  };

  const reconnect = () => {
    if (!mb) return;
    const provider = mb.provider;
    const cid = mb.client_id;
    window.location.href = `${API_BASE}/oauth/${provider}/connect?client_id=${cid}&mailbox_id=${mb.id}`;
  };

  if (loading) return <div style={{ padding: 24 }}>Loading mailbox…</div>;
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;
  if (!mb) return <div style={{ padding: 24 }}>Mailbox not found.</div>;

  const backLink = isGlobalMode ? '/mail/mailboxes' : `/clients/${clientId}/mail/mailboxes`;
  const isOAuth = ['google', 'microsoft', 'zoho'].includes(mb.provider);

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{mb.email_address}</h2>
        <StatusBadge status={mb.status} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666', fontSize: 13, marginBottom: 20 }}>
        <span style={{ textTransform: 'capitalize' }}>{mb.provider}</span>
        <span>·</span>
        <span style={{ textTransform: 'capitalize' }}>{mb.auth_type}</span>
        {client && (
          <>
            <span>·</span>
            <Link to={`/clients/${client.id}/mail/mailboxes`} style={{ color: '#8B1A1A', textDecoration: 'none' }}>
              {client.name}{client.sector ? ` · ${client.sector}` : ''}
            </Link>
          </>
        )}
      </div>

      {savedMsg && (
        <div style={{ background: '#E8F5EC', color: '#1a7f3c', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          {savedMsg}
        </div>
      )}

      <Section title="Connection">
        <Field label="Provider">{mb.provider}</Field>
        <Field label="Auth type">{mb.auth_type}</Field>
        {mb.parent_mailbox_id && (
          <Field label="Sends via">
            <span>{mb.parent_email_address || `mailbox #${mb.parent_mailbox_id}`}</span>
            <span style={{ marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 4, color: mb.parent_status === 'active' ? '#0E6E56' : '#b91c1c', background: mb.parent_status === 'active' ? '#E1F5EE' : '#FCEBEB' }}>
              parent {mb.parent_status === 'active' ? 'healthy' : 'in error'}
            </span>
          </Field>
        )}
        {mb.parent_mailbox_id && mb.parent_status !== 'active' && (
          <div style={{ margin: '8px 0', padding: '10px 12px', background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 6, fontSize: 13, color: '#7a1d1d' }}>
            This mailbox sends through its parent, which is currently in error. Reconnect the parent ({mb.parent_email_address || `#${mb.parent_mailbox_id}`}) and this mailbox recovers automatically. Refreshing this row alone will not fix it.
          </div>
        )}
        {mb.smtp_host && <Field label="SMTP">{mb.smtp_host}:{mb.smtp_port}</Field>}
        {mb.imap_host && <Field label="IMAP">{mb.imap_host}:{mb.imap_port}</Field>}
        <Field label="Last sync">{mb.last_sync_at ? new Date(mb.last_sync_at).toLocaleString('en-GB') : 'never'}</Field>
        <Field label="Connected">{mb.connected_at ? new Date(mb.connected_at).toLocaleString('en-GB') : '—'}</Field>
        {mb.last_error && (
          <Field label="Last error">
            <span style={{ color: '#b91c1c' }}>{mb.last_error}</span>
          </Field>
        )}
        {health && (
          <Field label="Health check">
            <span style={{ color: health.ok ? '#1a7f3c' : '#b91c1c' }}>
              {health.ok ? 'OK' : (health.error || 'failing')}
            </span>
          </Field>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {isOAuth && (
            <button onClick={reconnect} style={btnPrimary}>Reconnect via OAuth</button>
          )}
          {mb.status === 'active' && (
            <button onClick={() => action('/pause')} style={btnSecondary}>Pause</button>
          )}
          {(mb.status === 'paused' || mb.status === 'error') && (
            <button onClick={() => action('/resume')} style={btnSecondary}>Resume</button>
          )}
        </div>
      </Section>

      <Section title="Sending">
        <Field label="Sent today">{mb.sent_today ?? 0}{mb.sent_today_date ? ` (${new Date(mb.sent_today_date).toLocaleDateString('en-GB')})` : ''}</Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Daily cap</div>
            <input type="number" value={dailyCap} onChange={(e) => setDailyCap(parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Cap ceiling</div>
            <input type="number" value={dailyCapCeiling} onChange={(e) => setDailyCapCeiling(parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Ramp %/day</div>
            <input type="number" value={rampPct} onChange={(e) => setRampPct(parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#333', marginBottom: 12 }}>
          <input type="checkbox" checked={warmupEnabled} onChange={(e) => setWarmupEnabled(e.target.checked)} />
          Warmup enabled
        </label>
        <button
          onClick={() => save({ daily_cap: dailyCap, daily_cap_ceiling: dailyCapCeiling, ramp_pct: rampPct, warmup_enabled: warmupEnabled })}
          disabled={saving}
          style={btnPrimary}
        >
          {saving ? 'Saving…' : 'Save sending settings'}
        </button>
      </Section>

      <Section title="Identity">
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Display name</div>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Signature (HTML)</div>
          <textarea value={signatureHtml} onChange={(e) => setSignatureHtml(e.target.value)} rows={6} style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: 12 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Signature (plain text)</div>
          <textarea value={signatureText} onChange={(e) => setSignatureText(e.target.value)} rows={4} style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: 12 }} />
        </div>
        <button
          onClick={() => save({ display_name: displayName, signature_html: signatureHtml, signature_text: signatureText })}
          disabled={saving}
          style={btnPrimary}
        >
          {saving ? 'Saving…' : 'Save identity'}
        </button>
      </Section>

      <Section title="Used by personas">
        {personas.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888' }}>
            This mailbox isn't backing any persona. Add it to a persona from the Personas screen to send under a shared identity.
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
              Sends from this mailbox can go out under {personas.length === 1 ? 'this persona' : 'these personas'}. Removing the mailbox from a persona stops new sends choosing it; in-flight sequences stay on their assigned mailbox.
            </div>
            {personas.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid #eee', borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <Link to={`/clients/${p.client_id}/mail/personas`} style={{ color: '#8B1A1A', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                    {p.display_name}
                  </Link>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {p.client_name || 'Unknown client'} · 1 of {p.total_backing} backing mailbox{p.total_backing === '1' ? '' : 'es'}
                    {Number(p.total_backing) === 1 && <span style={{ color: '#b91c1c', marginLeft: 6 }}>— only backing, removing pauses the persona</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Danger zone">
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Disconnect wipes tokens but keeps the row for audit. Hard delete removes the mailbox entirely.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {mb.status !== 'disconnected' && (
            <button onClick={disconnect} style={btnDanger}>Disconnect</button>
          )}
          <button onClick={hardDelete} style={btnDangerSolid}>Delete permanently</button>
        </div>
      </Section>
    </div>
  );
}

const inputStyle = {
  padding: '8px 10px',
  border: '1px solid #D4CFC9',
  borderRadius: 5,
  fontSize: 13,
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: '#8B1A1A',
  color: 'white',
  border: 'none',
  borderRadius: 5,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnSecondary = {
  background: 'white',
  color: '#333',
  border: '1px solid #D4CFC9',
  borderRadius: 5,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnDanger = {
  background: 'white',
  color: '#b91c1c',
  border: '1px solid #b91c1c',
  borderRadius: 5,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnDangerSolid = {
  background: '#b91c1c',
  color: 'white',
  border: 'none',
  borderRadius: 5,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};
